import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { faListUl } from '@fortawesome/pro-regular-svg-icons/faListUl';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { FunctionComponent, KeyboardEvent } from 'react';
import styles from './DepositModeCards.module.scss';
import { DepositApplicability } from './types';

export interface DepositModeCardsProps {
    value: DepositApplicability;
    className?: string;
    onChange: (value: DepositApplicability) => void;
}

interface ModeCard {
    id: DepositApplicability;
    icon: IconProp;
    title: string;
    description: string;
}

const MODES: Array<ModeCard> = [
    {
        id: 'all',
        icon: faListUl,
        title: 'All Services',
        description: 'Require the same deposit on every service you offer.'
    },
    {
        id: 'specific',
        icon: faCheck,
        title: 'Specific Services',
        description: 'Set different deposit amounts by service, or require deposits on select services.'
    }
];

export const DepositModeCards: FunctionComponent<DepositModeCardsProps> = ({ value, className, onChange }) => {
    const keyDownHandler = (event: KeyboardEvent<HTMLDivElement>, mode: DepositApplicability) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onChange(mode);
        }
    };

    const renderCard = (mode: ModeCard) => {
        const active = value === mode.id;

        return (
            <div
                key={mode.id}
                role="radio"
                tabIndex={0}
                aria-checked={active}
                className={clsx(styles.card, { [styles.active]: active })}
                onClick={() => onChange(mode.id)}
                onKeyDown={event => keyDownHandler(event, mode.id)}
            >
                <FontAwesomeIcon
                    icon={mode.icon}
                    className={styles.cardIcon}
                />
                <div className={styles.cardText}>
                    <p className={styles.cardTitle}>{mode.title}</p>
                    <p className={styles.cardDescription}>{mode.description}</p>
                </div>
                <FontAwesomeIcon
                    icon={active ? faCircleCheck : faCircle}
                    className={clsx(styles.indicator, { [styles.indicatorActive]: active })}
                />
            </div>
        );
    };

    return (
        <div
            className={clsx(styles.container, className)}
            role="radiogroup"
            aria-label="Deposit applicability"
        >
            {renderCard(MODES[0])}
            <span className={styles.or}>or</span>
            {renderCard(MODES[1])}
        </div>
    );
};

export default DepositModeCards;
